import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import { 
  Network, 
  GitBranch, 
  ArrowRight, 
  Eye, 
  EyeOff, 
  Filter,
  AlertCircle,
  List
} from 'lucide-react';

const FeatureDependencyVisualization = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [viewMode, setViewMode] = useState('graph'); // 'graph' or 'tree'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showDetails, setShowDetails] = useState(false);
  const [dependencyMap, setDependencyMap] = useState({});

  // Category colors
  const categoryColors = {
    core: 'bg-blue-100 text-blue-800 border-blue-300',
    advanced: 'bg-purple-100 text-purple-800 border-purple-300',
    reporting: 'bg-green-100 text-green-800 border-green-300',
    integration: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    security: 'bg-red-100 text-red-800 border-red-300'
  };

  // Plan colors
  const planColors = {
    free: 'bg-gray-100 text-gray-800 border-gray-300',
    basic: 'bg-blue-100 text-blue-800 border-blue-300',
    premium: 'bg-purple-100 text-purple-800 border-purple-300',
    enterprise: 'bg-red-100 text-red-800 border-red-300'
  };

  const categories = ['all', 'core', 'advanced', 'reporting', 'integration', 'security'];

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getFeatures();
      const fetchedFeatures = response.data.features || [];
      setFeatures(fetchedFeatures);
      
      // Build dependency map
      const depMap = {};
      fetchedFeatures.forEach(feature => {
        depMap[feature.code] = {
          ...feature,
          dependents: [], // Features that depend on this feature
          dependsOn: fetchedFeatures.filter(f => 
            feature.dependencies && feature.dependencies.includes(f.code)
          )
        };
      });

      // Find dependents
      fetchedFeatures.forEach(feature => {
        if (feature.dependencies) {
          feature.dependencies.forEach(depCode => {
            if (depMap[depCode]) {
              depMap[depCode].dependents.push(feature);
            }
          });
        }
      });

      setDependencyMap(depMap);
    } catch (error) {
      console.error('Error fetching features:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredFeatures = features.filter(feature => 
    categoryFilter === 'all' || feature.category === categoryFilter
  );

  const getFeatureLevel = (feature) => {
    if (!feature.dependencies || feature.dependencies.length === 0) return 0;
    
    const depFeatures = features.filter(f => 
      feature.dependencies.includes(f.code)
    );
    
    if (depFeatures.length === 0) return 0;
    
    const maxDepLevel = Math.max(...depFeatures.map(getFeatureLevel));
    return maxDepLevel + 1;
  };

  const organizeFeaturesByLevel = () => {
    const levels = {};
    filteredFeatures.forEach(feature => {
      const level = getFeatureLevel(feature);
      if (!levels[level]) levels[level] = [];
      levels[level].push(feature);
    });
    return levels;
  };

  const FeatureNode = ({ feature, onClick, isSelected }) => (
    <div
      className={`
        relative p-4 border-2 rounded-lg cursor-pointer transition-all
        ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'}
        ${categoryColors[feature.category]}
        hover:shadow-md
      `}
      onClick={() => onClick(feature)}
    >
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-sm">{feature.name}</h4>
        <span className={`px-2 py-1 text-xs font-medium rounded border ${planColors[feature.requiredPlan]}`}>
          {feature.requiredPlan}
        </span>
      </div>
      
      <div className="text-xs text-gray-600 mb-2">
        {feature.description}
      </div>
      
      <div className="flex items-center justify-between">
        <span className="px-2 py-1 text-xs font-medium rounded border bg-gray-100 text-gray-800">
          {feature.category}
        </span>
        
        <div className="flex items-center space-x-1">
          {feature.dependencies && feature.dependencies.length > 0 && (
            <div className="flex items-center text-xs text-gray-500">
              <ArrowRight className="w-3 h-3 mr-1" />
              {feature.dependencies.length}
            </div>
          )}
          
          {dependencyMap[feature.code]?.dependents.length > 0 && (
            <div className="flex items-center text-xs text-blue-500">
              <GitBranch className="w-3 h-3 mr-1" />
              {dependencyMap[feature.code]?.dependents.length}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const GraphView = () => {
    const levels = organizeFeaturesByLevel();
    const levelKeys = Object.keys(levels).sort((a, b) => Number(a) - Number(b));

    return (
      <div className="space-y-8">
        {levelKeys.map(level => (
          <div key={level} className="space-y-4">
            <div className="flex items-center space-x-2">
              <span className="px-2 py-1 text-xs font-medium rounded border bg-gray-100 text-gray-800">
                Level {level}
              </span>
              <span className="text-xs text-gray-500">
                {level === '0' ? 'Core Features' : 
                 level === '1' ? 'First Level Dependencies' : 
                 `Level ${level} Dependencies`}
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {levels[level].map(feature => (
                <FeatureNode
                  key={feature.id}
                  feature={feature}
                  onClick={setSelectedFeature}
                  isSelected={selectedFeature?.id === feature.id}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const TreeView = () => {
    const coreFeatures = filteredFeatures.filter(f => !f.dependencies || f.dependencies.length === 0);
    
    const renderFeatureTree = (feature, level = 0) => {
      const dependents = dependencyMap[feature.code]?.dependents || [];
      
      return (
        <div key={feature.id} className="space-y-2">
          <div className={`flex items-center space-x-2 ${level > 0 ? 'ml-' + (level * 4) : ''}`}>
            <div className={`w-2 h-2 rounded-full ${level === 0 ? 'bg-blue-500' : 'bg-gray-400'}`} />
            <FeatureNode
              feature={feature}
              onClick={setSelectedFeature}
              isSelected={selectedFeature?.id === feature.id}
            />
          </div>
          
          {dependents.map(dependent => 
            renderFeatureTree(dependent, level + 1)
          )}
        </div>
      );
    };

    return (
      <div className="space-y-6">
        {coreFeatures.map(feature => renderFeatureTree(feature))}
      </div>
    );
  };

  const DetailsPanel = ({ feature }) => {
    if (!feature) return null;

    const dependsOn = dependencyMap[feature.code]?.dependsOn || [];
    const dependents = dependencyMap[feature.code]?.dependents || [];

    return (
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <div className="flex items-center space-x-2">
            <Network className="w-5 h-5" />
            <span className="text-lg font-semibold">{feature.name}</span>
            <span className={`px-2 py-1 text-xs font-medium rounded border ${planColors[feature.requiredPlan]}`}>
              {feature.requiredPlan}
            </span>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <h4 className="font-medium mb-2">Description</h4>
            <p className="text-sm text-gray-600">{feature.description}</p>
          </div>

          <div>
            <h4 className="font-medium mb-2">Category</h4>
            <span className={`px-2 py-1 text-xs font-medium rounded border ${categoryColors[feature.category]}`}>
              {feature.category}
            </span>
          </div>

          {dependsOn.length > 0 && (
            <div>
              <h4 className="font-medium mb-2 flex items-center">
                <ArrowRight className="w-4 h-4 mr-1" />
                Depends On ({dependsOn.length})
              </h4>
              <div className="space-y-2">
                {dependsOn.map(dep => (
                  <div key={dep.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm">{dep.name}</span>
                    <span className={`px-2 py-1 text-xs font-medium rounded border ${planColors[dep.requiredPlan]}`}>
                      {dep.requiredPlan}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {dependents.length > 0 && (
            <div>
              <h4 className="font-medium mb-2 flex items-center">
                <GitBranch className="w-4 h-4 mr-1" />
                Required By ({dependents.length})
              </h4>
              <div className="space-y-2">
                {dependents.map(dependent => (
                  <div key={dependent.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm">{dependent.name}</span>
                    <span className={`px-2 py-1 text-xs font-medium rounded border ${planColors[dependent.requiredPlan]}`}>
                      {dependent.requiredPlan}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(!feature.dependencies || feature.dependencies.length === 0) && dependents.length === 0 && (
            <div className="flex items-center space-x-2 text-gray-500">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">This feature has no dependencies</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Network className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold">Feature Dependencies</h2>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            className="px-3 py-1 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded text-sm flex items-center space-x-2"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span>{showDetails ? 'Hide' : 'Show'} Details</span>
          </button>
        </div>
      </div>

      <div className="flex items-center space-x-4 mb-6">
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {cat === 'all' ? 'All Categories' : cat.charAt(0).toUpperCase() + cat.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={showDetails && selectedFeature ? 'lg:col-span-2' : 'lg:col-span-3'}>
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6 border-b">
              <div className="flex space-x-1 border-b border-gray-200">
                <button
                  onClick={() => setViewMode('graph')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 ${
                    viewMode === 'graph'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Graph View
                </button>
                <button
                  onClick={() => setViewMode('tree')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 ${
                    viewMode === 'tree'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Tree View
                </button>
              </div>
            </div>
            <div className="p-6">
              {viewMode === 'graph' && <GraphView />}
              {viewMode === 'tree' && <TreeView />}
            </div>
          </div>
        </div>

        {showDetails && selectedFeature && (
          <div className="lg:col-span-1">
            <DetailsPanel feature={selectedFeature} />
          </div>
        )}
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-medium mb-2">Legend</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center space-x-2">
            <ArrowRight className="w-4 h-4 text-gray-500" />
            <span>Dependencies</span>
          </div>
          <div className="flex items-center space-x-2">
            <GitBranch className="w-4 h-4 text-blue-500" />
            <span>Dependents</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span>Core Features</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-gray-400"></div>
            <span>Dependent Features</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureDependencyVisualization; 